/**
 * Staying connected across a backgrounded game.
 *
 * Two different things go wrong when the player switches away, and this module is the shared
 * machinery for both.
 *
 * The first was ours. `client.update()` runs on the pixi ticker, which is requestAnimationFrame,
 * and a hidden tab is given no frames at all -- but `ws.onmessage` keeps firing, because a
 * background tab still receives network events. So `incomingWorldStates` filled at the server's
 * tick rate (~58 frames a second) with nothing on the other end to drain it, until the renderer
 * process was killed for its memory and took the socket down with it. From the player's seat the
 * websocket "died when the game was backgrounded"; what actually died was the tab. The answer is
 * to stop the stream at its SOURCE while nobody is drawing it (type_suspendUpdates) rather than to
 * buffer it more cleverly, which also hands the server back the broadcast work for an idle player.
 *
 * The second is not ours and cannot be prevented: iOS and Android suspend a backgrounded app and
 * the socket goes with it. What can be fixed is NOTICING. A resumed WebView routinely holds a
 * socket that still reports OPEN, will never deliver another byte, and never fires `close` -- so
 * nothing tells the client anything is wrong and the game just sits there. Hence the watchdog:
 * traffic is the liveness signal, and silence while the page is in the FOREGROUND is the symptom.
 *
 * Both reconnect, so one object owns every reason to dial. Two schedulers racing here would
 * double-dial, and on this server a second socket is a second session.
 */

/** The events that mean "the player may be looking at this again". */
export type LifecycleEvent = "visibilitychange" | "focus" | "online" | "pageshow"

/** Everything this needs from the outside world, so the policy above can be tested on its own. */
export interface LifecycleHost {
    /** Is the page backgrounded right now? */
    hidden: () => boolean
    now: () => number
    setTimer: (fn: () => void, ms: number) => any
    clearTimer: (handle: any) => void
    setPoll: (fn: () => void, ms: number) => any
    clearPoll: (handle: any) => void
    /** Subscribe to one lifecycle event; returns its teardown. */
    listen: (type: LifecycleEvent, fn: () => void) => () => void
}

/** What the lifecycle asks the client to do. */
export interface LifecycleDelegate {
    /** Tell the server to stop sending world state -- nobody is drawing it. */
    suspendUpdates: () => void
    /**
     * Tell the server to start again AND to resend everything. A resume cannot just pick the
     * stream back up: world state is a DELTA stream, so every frame missed during the background
     * is a change this client will never hear about again -- a chopped tree still standing, an
     * NPC drawn where it used to be. The gap has to be closed by a full resend, not by catching up.
     */
    resumeUpdates: () => void
    /** Throw the socket away and dial again. */
    reconnect: () => void
    /** Whether the socket is open as far as the browser is concerned. */
    isConnected: () => boolean
    /** Somewhere to say why a reconnect is happening; diagnosing this from a phone is otherwise guesswork. */
    log?: (message: string) => void
}

export interface LifecycleTimings {
    /**
     * How long the page must stay hidden before the stream is stopped. An alt-tab back and forth
     * should cost nothing: a resume forces a full resend, which is a burst of work on a one-core
     * box, so it is not something to trigger every time a player checks another window.
     */
    hiddenGraceMs: number
    /**
     * Foreground silence that means the socket is hung. The server pings every 5s on its own
     * (ClientProxy.startPing), so three missed pings is already well past any real hiccup.
     */
    staleAfterMs: number
    /**
     * The same judgement in the moments after a resume, where a resync burst is already owed to
     * us: nothing arriving promptly means the socket did not survive the background after all.
     * Shorter, because this is the common mobile case and the player is staring at a frozen game.
     */
    resumeStaleAfterMs: number
    /** How often the watchdog looks. */
    checkEveryMs: number
    /** Backoff between redials, in order. The last entry repeats. */
    retryDelaysMs: number[]
}

export const DEFAULT_TIMINGS: LifecycleTimings = {
    hiddenGraceMs: 2000,
    staleAfterMs: 15000,
    resumeStaleAfterMs: 5000,
    checkEveryMs: 2500,
    retryDelaysMs: [1000, 2000, 4000, 8000, 15000],
}

export const browserLifecycleHost = (): LifecycleHost => ({
    // visibilityState is the modern spelling; document.hidden is kept for older Android WebViews,
    // which is the environment that most needs this to work.
    hidden: () =>
        typeof document === "undefined"
            ? false
            : document.visibilityState
            ? document.visibilityState === "hidden"
            : !!document.hidden,
    now: () => Date.now(),
    setTimer: (fn, ms) => setTimeout(fn, ms),
    clearTimer: handle => clearTimeout(handle),
    setPoll: (fn, ms) => setInterval(fn, ms),
    clearPoll: handle => clearInterval(handle),
    listen: (type, fn) => {
        // visibilitychange is a document event; the rest are window events. Getting this wrong
        // fails silently, which is the worst way for a reconnect path to fail.
        const target: any = type === "visibilitychange" ? document : window
        target.addEventListener(type, fn)
        return () => target.removeEventListener(type, fn)
    },
})

export class ConnectionLifecycle {
    private readonly delegate: LifecycleDelegate
    private readonly host: LifecycleHost
    private readonly timings: LifecycleTimings

    private teardowns: (() => void)[] = []
    private pollHandle: any
    private hiddenHandle: any
    private retryHandle: any

    /** True once the server has been told to stop sending. */
    private suspended = false
    /** Last moment anything at all arrived from the server. */
    private lastTrafficTs = 0
    /** Silence currently allowed before the socket is called hung; tightened just after a resume. */
    private staleAfterMs: number
    /** Consecutive failed redials, for the backoff. */
    private retryCount = 0
    /** A dial is in flight. Nothing else may start one. */
    private dialling = false

    constructor(delegate: LifecycleDelegate, host: LifecycleHost = browserLifecycleHost(), timings = DEFAULT_TIMINGS) {
        this.delegate = delegate
        this.host = host
        this.timings = timings
        this.staleAfterMs = timings.staleAfterMs
        this.lastTrafficTs = host.now()
    }

    start = () => {
        if (this.teardowns.length > 0) {
            return
        }
        this.teardowns.push(
            this.host.listen("visibilitychange", () => {
                if (this.host.hidden()) {
                    this.armHiddenTimer()
                } else {
                    this.resumed()
                }
            }),
        )
        // Belt and braces around visibilitychange, which a WebView does not always deliver on the
        // way back in. Each of these is a resume in its own right, and `resumed` is idempotent.
        const alsoResume: LifecycleEvent[] = ["focus", "online", "pageshow"]
        alsoResume.forEach(type =>
            this.teardowns.push(
                this.host.listen(type, () => {
                    if (!this.host.hidden()) {
                        this.resumed()
                    }
                }),
            ),
        )
        this.pollHandle = this.host.setPoll(this.check, this.timings.checkEveryMs)
    }

    stop = () => {
        this.teardowns.forEach(teardown => teardown())
        this.teardowns = []
        this.host.clearPoll(this.pollHandle)
        this.pollHandle = undefined
        this.clearHiddenTimer()
        this.clearRetryTimer()
    }

    /** Anything arrived from the server. The one signal that the socket is really alive. */
    noteTraffic = () => {
        this.lastTrafficTs = this.host.now()
        this.staleAfterMs = this.timings.staleAfterMs
    }

    /** A socket opened. */
    noteConnected = () => {
        this.dialling = false
        this.retryCount = 0
        this.suspended = false
        this.noteTraffic()
        // A socket that finishes opening when the page is ALREADY hidden -- the player dialled and
        // switched away during the handshake -- still has a stream to stop.
        if (this.host.hidden()) {
            this.armHiddenTimer()
        }
    }

    /** The current socket closed, or a dial failed. */
    noteConnectionLost = () => {
        const dialFailed = this.dialling
        this.dialling = false
        this.suspended = false
        this.clearHiddenTimer()
        // A failed retry backs off; a live socket dying starts the ladder from the bottom, because
        // most lost sockets come back on the first try.
        this.retryCount = dialFailed ? this.retryCount + 1 : 0
        this.scheduleRetry()
    }

    /** The player pressed "Try again". Skip the ladder. */
    retryNow = () => {
        this.clearRetryTimer()
        this.retryCount = 0
        this.dial("player asked")
    }

    private armHiddenTimer = () => {
        if (this.hiddenHandle || this.suspended) {
            return
        }
        this.hiddenHandle = this.host.setTimer(() => {
            this.hiddenHandle = undefined
            // Back already, or gone before the socket was up: either way there is nothing to stop.
            if (!this.host.hidden() || this.suspended || !this.delegate.isConnected()) {
                return
            }
            this.suspended = true
            this.delegate.suspendUpdates()
        }, this.timings.hiddenGraceMs)
    }

    private clearHiddenTimer = () => {
        if (this.hiddenHandle) {
            this.host.clearTimer(this.hiddenHandle)
            this.hiddenHandle = undefined
        }
    }

    private clearRetryTimer = () => {
        if (this.retryHandle) {
            this.host.clearTimer(this.retryHandle)
            this.retryHandle = undefined
        }
    }

    private resumed = () => {
        this.clearHiddenTimer()

        if (!this.delegate.isConnected()) {
            // The socket did not survive. Dial straight away rather than serving out a backoff the
            // player experiences as the game having hung.
            this.clearRetryTimer()
            this.retryCount = 0
            this.dial("resumed on a closed socket")
            return
        }

        if (this.suspended) {
            this.suspended = false
            this.delegate.resumeUpdates()
            // We have just asked for a resend, so it is owed immediately. If nothing comes, this
            // socket is one of the corpses that still reports OPEN.
            this.lastTrafficTs = this.host.now()
            this.staleAfterMs = this.timings.resumeStaleAfterMs
            return
        }

        // Never suspended (a quick flick away), so the stream never stopped and the ordinary
        // deadline applies -- but the clock restarts here, because any silence up to now was the
        // background's and says nothing about the socket.
        this.lastTrafficTs = this.host.now()
        this.staleAfterMs = this.timings.staleAfterMs
    }

    private scheduleRetry = () => {
        if (this.retryHandle || this.dialling) {
            return
        }
        // No dialling while hidden: on a suspended phone it cannot succeed, and on a desktop
        // nobody is watching the result. `resumed` dials immediately instead.
        if (this.host.hidden()) {
            return
        }
        const delays = this.timings.retryDelaysMs
        const delay = delays[Math.min(this.retryCount, delays.length - 1)]
        this.retryHandle = this.host.setTimer(() => {
            this.retryHandle = undefined
            this.dial(`retry ${this.retryCount + 1}`)
        }, delay)
    }

    private dial = (why: string) => {
        if (this.dialling) {
            return
        }
        this.dialling = true
        // Start the liveness clock from the dial, so a socket that opens and says nothing is
        // judged on its own silence rather than on the previous socket's.
        this.lastTrafficTs = this.host.now()
        this.staleAfterMs = this.timings.staleAfterMs
        this.delegate.log?.(`reconnecting: ${why}`)
        this.delegate.reconnect()
    }

    private check = () => {
        // Nothing is being drawn and the stream was stopped on purpose, so silence here is
        // expected rather than evidence. Background timers are throttled to a crawl anyway.
        if (this.host.hidden() || this.dialling) {
            return
        }
        if (!this.delegate.isConnected()) {
            // A close we were never told about, or a retry that is overdue.
            this.scheduleRetry()
            return
        }
        if (this.host.now() - this.lastTrafficTs < this.staleAfterMs) {
            return
        }
        // Open, in the foreground, and silent for longer than the server's own ping interval
        // allows. This is the resumed-but-dead socket: no close event is ever coming for it.
        this.retryCount = 0
        this.dial("no traffic")
    }
}
